import { useState, useEffect } from 'react'
import { useNotifications } from '@/contexts/NotificationContext'
import { isNativePlatform } from '@/utils/platform'
import { isCalendarAvailable, requestCalendarPermission, checkCalendarPermission, openNativeSettings } from '@/utils/calendarEvents'

function Settings({ displayName, onUpdateDisplayName, defaultDayStart, defaultDayEnd, onUpdateDayDefaults }) {
  const { notificationsEnabled, setNotificationsEnabled, medRemindersEnabled, setMedRemindersEnabled, permissionState } = useNotifications()
  const [name, setName] = useState(displayName)
  const [dayStart, setDayStart] = useState(defaultDayStart || '08:00')
  const [dayEnd, setDayEnd] = useState(defaultDayEnd || '17:00')
  const [savedFlash, setSavedFlash] = useState(false)

  // Calendar integration state
  const CALENDAR_STORAGE_KEY = 'adhd-calendar-sync-enabled'
  const [calendarSyncEnabled, setCalendarSyncEnabledState] = useState(() => {
    try { return localStorage.getItem(CALENDAR_STORAGE_KEY) === 'true' } catch { return false }
  })
  const [calendarPermission, setCalendarPermission] = useState('denied')
  // True when the user has tried to enable sync but access isn't granted,
  // so we surface the "grant access in iOS Settings" guidance.
  const [calendarBlocked, setCalendarBlocked] = useState(false)

  useEffect(() => {
    if (isCalendarAvailable()) {
      checkCalendarPermission().then((permission) => {
        setCalendarPermission(permission)
        // If sync was previously on but access has since been revoked, warn.
        if (permission !== 'granted' && localStorage.getItem(CALENDAR_STORAGE_KEY) === 'true') {
          setCalendarBlocked(true)
        }
      })
    }
  }, [])

  const isDirty =
    name.trim() !== (displayName || '') ||
    dayStart !== (defaultDayStart || '08:00') ||
    dayEnd !== (defaultDayEnd || '17:00')

  function persistCalendarSync(enabled) {
    setCalendarSyncEnabledState(enabled)
    try { localStorage.setItem(CALENDAR_STORAGE_KEY, String(enabled)) } catch {}
  }

  async function handleCalendarToggle(enabled) {
    if (!enabled) {
      setCalendarBlocked(false)
      persistCalendarSync(false)
      return
    }

    // Turning on: re-run the permission flow if access isn't already granted.
    let permission = await checkCalendarPermission()
    if (permission === 'prompt' || permission === 'prompt-with-rationale') {
      // Not yet decided — iOS will show the system prompt.
      permission = await requestCalendarPermission()
    } else if (permission !== 'granted') {
      // Previously denied — iOS won't re-prompt, so requesting again is a no-op,
      // but we still call it in case the state is stale (e.g. reset permissions).
      permission = await requestCalendarPermission()
    }
    setCalendarPermission(permission)

    if (permission !== 'granted') {
      // Leave the toggle off and guide the user to iOS Settings.
      setCalendarBlocked(true)
      return
    }

    setCalendarBlocked(false)
    persistCalendarSync(true)
  }

  function handleSave() {
    onUpdateDisplayName(name.trim())
    onUpdateDayDefaults(dayStart, dayEnd)
    setSavedFlash(true)
    setTimeout(() => setSavedFlash(false), 2000)
  }

  function handleDiscard() {
    setName(displayName || '')
    setDayStart(defaultDayStart || '08:00')
    setDayEnd(defaultDayEnd || '17:00')
  }

  return (
    <div className={`page settings${isDirty ? ' settings--has-unsaved' : ''}`}>
      <h2 className="page-title">Settings</h2>

      {/* General */}
      <div className="settings-group">
        <h3 className="settings-group-header">General</h3>
        <div className="settings-card">
          <div className="settings-row">
            <div className="settings-row-label">
              <span className="settings-label-text">Display name</span>
            </div>
            <input
              type="text"
              className="settings-row-input"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="settings-row">
            <div className="settings-row-label">
              <span className="settings-label-text">Day start time</span>
            </div>
            <input
              type="time"
              className="settings-row-input settings-row-input--time"
              value={dayStart}
              onChange={(e) => setDayStart(e.target.value)}
            />
          </div>
          <div className="settings-row">
            <div className="settings-row-label">
              <span className="settings-label-text">Day end time</span>
            </div>
            <input
              type="time"
              className="settings-row-input settings-row-input--time"
              value={dayEnd}
              onChange={(e) => setDayEnd(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Integrations */}
      <div className="settings-group">
        <h3 className="settings-group-header">Integrations</h3>
        <div className="settings-card">
          {isCalendarAvailable() && (
            <div className="settings-row settings-row--toggle">
              <div className="settings-row-label">
                <span className="settings-label-text">Calendar sync</span>
                <span className="settings-label-hint">
                  Show device calendar events in your daily schedule
                </span>
                {calendarBlocked && calendarPermission !== 'granted' && (
                  <span className="settings-row-status settings-row-status--warn">
                    Calendar access is off. Enable it in Settings &rsaquo; Nudg.U &rsaquo; Calendars, then turn this on.
                    <button
                      type="button"
                      className="settings-inline-link"
                      onClick={openNativeSettings}
                    >
                      Open Settings
                    </button>
                  </span>
                )}
                {calendarSyncEnabled && calendarPermission === 'granted' && (
                  <span className="settings-row-status">
                    Your calendar events will appear in your daily schedule.
                  </span>
                )}
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={calendarSyncEnabled}
                  onChange={(e) => handleCalendarToggle(e.target.checked)}
                />
                <span className="toggle-slider" />
              </label>
            </div>
          )}
          <div className="settings-row settings-row--toggle">
            <div className="settings-row-label">
              <span className="settings-label-text">Task reminders</span>
              <span className="settings-label-hint">
                Get notified when it's time to start a scheduled task
              </span>
              {notificationsEnabled && permissionState === 'denied' && (
                <span className="settings-row-status settings-row-status--warn">
                  {isNativePlatform()
                    ? 'Notifications disabled — open Settings \u203a Nudg.U \u203a Notifications'
                    : "You'll receive in-app alerts while the app is open."}
                </span>
              )}
              {notificationsEnabled && permissionState === 'granted' && (
                <span className="settings-row-status">
                  {isNativePlatform()
                    ? "You'll receive notifications even when the app is closed."
                    : "You'll receive in-app alerts when it's time to start a task."}
                </span>
              )}
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationsEnabled}
                onChange={(e) => setNotificationsEnabled(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
          </div>
          <div className="settings-row settings-row--toggle">
            <div className="settings-row-label">
              <span className="settings-label-text">Medication reminders</span>
              <span className="settings-label-hint">
                Get reminded when it's time to take your medication
              </span>
              {medRemindersEnabled && permissionState === 'denied' && (
                <span className="settings-row-status settings-row-status--warn">
                  {isNativePlatform()
                    ? 'Notifications disabled \u2014 open Settings \u203a Nudg.U \u203a Notifications'
                    : "You'll receive in-app alerts while the app is open."}
                </span>
              )}
              {medRemindersEnabled && permissionState === 'granted' && (
                <span className="settings-row-status">
                  {isNativePlatform()
                    ? "You'll receive reminders even when the app is closed."
                    : "You'll receive in-app alerts at medication time."}
                </span>
              )}
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={medRemindersEnabled}
                onChange={(e) => setMedRemindersEnabled(e.target.checked)}
              />
              <span className="toggle-slider" />
            </label>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="settings-group">
        <h3 className="settings-group-header">Support</h3>
        <div className="settings-card">
          <a
            className="settings-row settings-row--link"
            href="mailto:nudg.u@marshall.us.com?subject=Nudg.U%20Beta%20Feedback"
            onClick={(e) => {
              e.preventDefault()
              window.location.href = 'mailto:nudg.u@marshall.us.com?subject=Nudg.U%20Beta%20Feedback'
            }}
          >
            <span className="settings-label-text">Send feedback</span>
            <span className="settings-row-chevron">&rsaquo;</span>
          </a>
        </div>
      </div>

      {/* Save / Discard bar */}
      {isDirty && !savedFlash && (
        <div className="settings-save-bar">
          <button className="btn btn-secondary" onClick={handleDiscard}>Discard</button>
          <button className="btn btn-primary" onClick={handleSave}>Save</button>
        </div>
      )}
      {savedFlash && (
        <div className="settings-save-bar settings-save-bar--flash">
          <span className="settings-save-flash">Saved!</span>
        </div>
      )}
    </div>
  )
}

export default Settings
